import React, { PropsWithChildren, RefAttributes, useRef, useState, useCallback, useEffect } from 'react';

import { cn, ClassName, ComponentWrapperProps, animation, multipleRef } from '../../utils';
import styles from './index.module.scss';
import { Text } from '../Typography';
import { Portal } from '../Portal';
import { TransitionComponent } from '../TransitionComponent';
import { getPosition, IPosition, Positions } from './utils';
import { useRemUpdatedCallback } from '../SizeContext';


export interface TooltipProps {
    tooltipClassName?: ClassName;
    tooltip: React.ReactChild;
    position?: Positions;
    size?: 'sm' | 'md' | 'lg' | 'dn';
    disabled?: boolean;
    rect?: boolean;
    usePortal?: boolean;
}

const TooltipWithPortal = <PComponent extends PropsWithChildren<ClassName>>({
    component, componentRef,
    position, tooltip, size, disabled, rect, // TooltipProps
    className, tooltipClassName, children, ...props // PComponent
}: Omit<ComponentWrapperProps<PComponent, TooltipProps>, 'usePortal'>) => {
    const [isHover, setIsHover] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState<IPosition | null>(null);

    const containerRef = useRef<HTMLElement>(null);
    const tooltipRef = useRef<HTMLElement>(null);

    const calculatePosition = useCallback(
        () => {
            if (!containerRef.current || !tooltipRef.current)
                return;
            setTooltipPosition(getPosition(containerRef.current, tooltipRef.current, position));
        },
        [containerRef, tooltipRef, position],
    );

    const leave = () => {
        setIsHover(false);
        setTooltipPosition(null);
    };

    useEffect(() => {
        if (isHover && tooltipRef)
            calculatePosition();
    }, [isHover, tooltipRef, calculatePosition]);

    useRemUpdatedCallback(calculatePosition);

    return React.createElement(
        component,
        {
            ...props,
            ref: multipleRef(componentRef, containerRef),
            className: cn(!disabled && styles.container, className),
            onMouseOver: () => setIsHover(true),
            onMouseLeave: leave,
        } as any as PComponent & RefAttributes<any>,
        <>
            {children}
            {!disabled && (
                <Portal>
                    <TransitionComponent
                        in={isHover}
                        timeout={animation}
                    >
                        <Text
                            componentRef={tooltipRef}
                            component='div'
                            style={{
                                top: tooltipPosition?.top,
                                bottom: tooltipPosition?.bottom,
                                left: tooltipPosition?.left,
                            }}
                            className={cn(
                                styles.tooltip,
                                styles[tooltipPosition?.resultPos],
                                styles[size],
                                tooltipClassName,
                                rect && styles.rect,
                                styles.withPortal,
                            )}
                        >
                            {tooltip}
                        </Text>
                    </TransitionComponent>
                </Portal>
            )}
        </>,
    );
};

const TooltipBase = <PComponent extends PropsWithChildren<ClassName>>({
    component, componentRef,
    position, tooltip, size, disabled, rect, // TooltipProps
    className, tooltipClassName, children, ...props // PComponent
}: Omit<ComponentWrapperProps<PComponent, TooltipProps>, 'usePortal'>) => React.createElement(
    component,
    {
        ...props,
        ref: componentRef,
        className: cn(!disabled && styles.container, className),
    } as any as PComponent & RefAttributes<any>,
    <>
        {children}
        {!disabled && (
            <Text
                component='div'
                className={cn(
                    styles.tooltip,
                    styles[position],
                    styles[size],
                    tooltipClassName,
                    rect && styles.rect,
                )}
            >
                {tooltip}
            </Text>
        )}
    </>,
);

export const Tooltip = <PComponent extends PropsWithChildren<ClassName>>({
    usePortal, ...props
}: ComponentWrapperProps<PComponent, TooltipProps>) =>
    usePortal ? <TooltipWithPortal {...props} /> : <TooltipBase {...props} />;

Tooltip.defaultProps = {
    position: 'right',
    size: 'md',
};
